import { classnames } from '@/helpers';
import React, { forwardRef, Fragment, useEffect, useId, useImperativeHandle, useRef, useState } from 'react';

type CheckboxState = 'checked' | 'indeterminate' | 'unchecked';

export type CheckboxProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> & {
  checked?: boolean;
  defaultChecked?: boolean;
  indeterminate?: boolean;
  label?: React.ReactNode;
  onChange?: (nextChecked: boolean, ev: React.ChangeEvent<HTMLInputElement>) => void;
  className?: string; // extra classes on <label>
};

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { checked, defaultChecked, indeterminate, disabled, label, onChange, id, className, ...rest },
  ref,
) {
  const internalRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => internalRef.current as HTMLInputElement);

  const [uncontrolled, setUncontrolled] = useState<boolean>(!!defaultChecked);
  const isControlled = checked !== undefined;
  const isChecked = isControlled ? !!checked : uncontrolled;

  useEffect(() => {
    if (internalRef.current) internalRef.current.indeterminate = !!indeterminate;
  }, [indeterminate]);

  const uid = useId();
  const inputId = id ?? `cb-${uid}`;

  const state: CheckboxState = indeterminate ? 'indeterminate' : isChecked ? 'checked' : 'unchecked';

  const handleChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    ev.stopPropagation();
    if (!isControlled) setUncontrolled(ev.target.checked);
    onChange?.(ev.target.checked, ev);
  };

  return (
    <label
      htmlFor={inputId}
      className={classnames('group mb-0 select-none', disabled ? 'cursor-not-allowed' : 'cursor-pointer', className)}
      data-state={state}>
      <input
        {...rest}
        id={inputId}
        ref={internalRef}
        type="checkbox"
        className="peer sr-only"
        disabled={disabled}
        checked={isControlled ? isChecked : undefined}
        defaultChecked={isControlled ? undefined : defaultChecked}
        onChange={handleChange}
      />

      {label}

      {/* Visual box */}
      <span
        aria-hidden="true"
        className={classnames('p-xs grid h-[40px] w-[32px] place-content-center peer-focus-visible:ring-2')}>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          {!isChecked && (
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M0 10C0 1.765 1.765 0 10 0C18.235 0 20 1.765 20 10C20 18.235 18.235 20 10 20C1.765 20 0 18.235 0 10ZM1.5 10C1.5 7.97549 1.61044 6.45099 1.86873 5.28878C2.12323 4.1436 2.50092 3.44415 2.97254 2.97254C3.44415 2.50092 4.1436 2.12323 5.28878 1.86873C6.45099 1.61044 7.97549 1.5 10 1.5C12.0245 1.5 13.549 1.61044 14.7112 1.86873C15.8564 2.12323 16.5558 2.50092 17.0275 2.97254C17.4991 3.44415 17.8768 4.1436 18.1313 5.28878C18.3896 6.45099 18.5 7.97549 18.5 10C18.5 12.0245 18.3896 13.549 18.1313 14.7112C17.8768 15.8564 17.4991 16.5558 17.0275 17.0275C16.5558 17.4991 15.8564 17.8768 14.7112 18.1313C13.549 18.3896 12.0245 18.5 10 18.5C7.97549 18.5 6.45099 18.3896 5.28878 18.1313C4.1436 17.8768 3.44415 17.4991 2.97254 17.0275C2.50092 16.5558 2.12323 15.8564 1.86873 14.7112C1.61044 13.549 1.5 12.0245 1.5 10Z"
              className={classnames(disabled && 'fill-fg_disabled', !disabled && 'fill-fg_primary')}
            />
          )}
          {isChecked && disabled && (
            <Fragment>
              {!indeterminate && (
                <Fragment>
                  <path
                    d="M14.5529 7.5068C14.8328 7.20146 14.8121 6.72703 14.5068 6.44714C14.2015 6.16724 13.727 6.18787 13.4471 6.49321L8.47644 11.9158L6.53033 9.96967C6.23744 9.67678 5.76256 9.67678 5.46967 9.96967C5.17678 10.2626 5.17678 10.7374 5.46967 11.0303L7.96967 13.5303C8.11432 13.675 8.31178 13.7543 8.51629 13.7498C8.72081 13.7454 8.91464 13.6576 9.05287 13.5068L14.5529 7.5068Z"
                    className="fill-fg_disabled"
                  />
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M0 10C0 1.765 1.765 0 10 0C18.235 0 20 1.765 20 10C20 18.235 18.235 20 10 20C1.765 20 0 18.235 0 10ZM1.5 10C1.5 7.97549 1.61044 6.45099 1.86873 5.28878C2.12323 4.1436 2.50092 3.44415 2.97254 2.97254C3.44415 2.50092 4.1436 2.12323 5.28878 1.86873C6.45099 1.61044 7.97549 1.5 10 1.5C12.0245 1.5 13.549 1.61044 14.7112 1.86873C15.8564 2.12323 16.5558 2.50092 17.0275 2.97254C17.4991 3.44415 17.8768 4.1436 18.1313 5.28878C18.3896 6.45099 18.5 7.97549 18.5 10C18.5 12.0245 18.3896 13.549 18.1313 14.7112C17.8768 15.8564 17.4991 16.5558 17.0275 17.0275C16.5558 17.4991 15.8564 17.8768 14.7112 18.1313C13.549 18.3896 12.0245 18.5 10 18.5C7.97549 18.5 6.45099 18.3896 5.28878 18.1313C4.1436 17.8768 3.44415 17.4991 2.97254 17.0275C2.50092 16.5558 2.12323 15.8564 1.86873 14.7112C1.61044 13.549 1.5 12.0245 1.5 10Z"
                    className="fill-fg_disabled"
                  />
                </Fragment>
              )}
              {indeterminate && (
                <Fragment>
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M0 10C0 1.765 1.765 0 10 0C18.235 0 20 1.765 20 10C20 18.235 18.235 20 10 20C1.765 20 0 18.235 0 10ZM1.5 10C1.5 7.97549 1.61044 6.45099 1.86873 5.28878C2.12323 4.1436 2.50092 3.44415 2.97254 2.97254C3.44415 2.50092 4.1436 2.12323 5.28878 1.86873C6.45099 1.61044 7.97549 1.5 10 1.5C12.0245 1.5 13.549 1.61044 14.7112 1.86873C15.8564 2.12323 16.5558 2.50092 17.0275 2.97254C17.4991 3.44415 17.8768 4.1436 18.1313 5.28878C18.3896 6.45099 18.5 7.97549 18.5 10C18.5 12.0245 18.3896 13.549 18.1313 14.7112C17.8768 15.8564 17.4991 16.5558 17.0275 17.0275C16.5558 17.4991 15.8564 17.8768 14.7112 18.1313C13.549 18.3896 12.0245 18.5 10 18.5C7.97549 18.5 6.45099 18.3896 5.28878 18.1313C4.1436 17.8768 3.44415 17.4991 2.97254 17.0275C2.50092 16.5558 2.12323 15.8564 1.86873 14.7112C1.61044 13.549 1.5 12.0245 1.5 10Z"
                    className="fill-fg_disabled"
                  />
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M5 10C5 9.58579 5.33579 9.25 5.75 9.25H13.75C14.1642 9.25 14.5 9.58579 14.5 10C14.5 10.4142 14.1642 10.75 13.75 10.75H5.75C5.33579 10.75 5 10.4142 5 10Z"
                    className="fill-fg_disabled"
                  />
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M5.75 9.25C5.33579 9.25 5 9.58579 5 10C5 10.4142 5.33579 10.75 5.75 10.75H13.75C14.1642 10.75 14.5 10.4142 14.5 10C14.5 9.58579 14.1642 9.25 13.75 9.25H5.75Z"
                    className="fill-fg_disabled"
                  />
                </Fragment>
              )}
            </Fragment>
          )}
          {isChecked && !disabled && (
            <Fragment>
              {!indeterminate && (
                <Fragment>
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M0 10C0 1.765 1.765 0 10 0C18.235 0 20 1.765 20 10C20 18.235 18.235 20 10 20C1.765 20 0 18.235 0 10ZM1.5 10C1.5 7.97549 1.61044 6.45099 1.86873 5.28878C2.12323 4.1436 2.50092 3.44415 2.97254 2.97254C3.44415 2.50092 4.1436 2.12323 5.28878 1.86873C6.45099 1.61044 7.97549 1.5 10 1.5C12.0245 1.5 13.549 1.61044 14.7112 1.86873C15.8564 2.12323 16.5558 2.50092 17.0275 2.97254C17.4991 3.44415 17.8768 4.1436 18.1313 5.28878C18.3896 6.45099 18.5 7.97549 18.5 10C18.5 12.0245 18.3896 13.549 18.1313 14.7112C17.8768 15.8564 17.4991 16.5558 17.0275 17.0275C16.5558 17.4991 15.8564 17.8768 14.7112 18.1313C13.549 18.3896 12.0245 18.5 10 18.5C7.97549 18.5 6.45099 18.3896 5.28878 18.1313C4.1436 17.8768 3.44415 17.4991 2.97254 17.0275C2.50092 16.5558 2.12323 15.8564 1.86873 14.7112C1.61044 13.549 1.5 12.0245 1.5 10Z"
                    className="fill-fg_brand"
                  />
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M1.86873 5.28878C1.61044 6.45099 1.5 7.97549 1.5 10C1.5 12.0245 1.61044 13.549 1.86873 14.7112C2.12323 15.8564 2.50092 16.5558 2.97254 17.0275C3.44415 17.4991 4.1436 17.8768 5.28878 18.1313C6.45099 18.3896 7.97549 18.5 10 18.5C12.0245 18.5 13.549 18.3896 14.7112 18.1313C15.8564 17.8768 16.5558 17.4991 17.0275 17.0275C17.4991 16.5558 17.8768 15.8564 18.1313 14.7112C18.3896 13.549 18.5 12.0245 18.5 10C18.5 7.97549 18.3896 6.45099 18.1313 5.28878C17.8768 4.1436 17.4991 3.44415 17.0275 2.97254C16.5558 2.50092 15.8564 2.12323 14.7112 1.86873C13.549 1.61044 12.0245 1.5 10 1.5C7.97549 1.5 6.45099 1.61044 5.28878 1.86873C4.1436 2.12323 3.44415 2.50092 2.97254 2.97254C2.50092 3.44415 2.12323 4.1436 1.86873 5.28878ZM14.5529 7.5068C14.8328 7.20146 14.8121 6.72703 14.5068 6.44714C14.2015 6.16724 13.727 6.18787 13.4471 6.49321L8.47644 11.9158L6.53033 9.96967C6.23744 9.67678 5.76256 9.67678 5.46967 9.96967C5.17678 10.2626 5.17678 10.7374 5.46967 11.0303L7.96967 13.5303C8.11432 13.675 8.31178 13.7543 8.51629 13.7498C8.72081 13.7454 8.91464 13.6576 9.05287 13.5068L14.5529 7.5068Z"
                    className="fill-fg_brand"
                  />
                </Fragment>
              )}
              {indeterminate && (
                <Fragment>
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M0 10C0 1.765 1.765 0 10 0C18.235 0 20 1.765 20 10C20 18.235 18.235 20 10 20C1.765 20 0 18.235 0 10ZM1.5 10C1.5 7.97549 1.61044 6.45099 1.86873 5.28878C2.12323 4.1436 2.50092 3.44415 2.97254 2.97254C3.44415 2.50092 4.1436 2.12323 5.28878 1.86873C6.45099 1.61044 7.97549 1.5 10 1.5C12.0245 1.5 13.549 1.61044 14.7112 1.86873C15.8564 2.12323 16.5558 2.50092 17.0275 2.97254C17.4991 3.44415 17.8768 4.1436 18.1313 5.28878C18.3896 6.45099 18.5 7.97549 18.5 10C18.5 12.0245 18.3896 13.549 18.1313 14.7112C17.8768 15.8564 17.4991 16.5558 17.0275 17.0275C16.5558 17.4991 15.8564 17.8768 14.7112 18.1313C13.549 18.3896 12.0245 18.5 10 18.5C7.97549 18.5 6.45099 18.3896 5.28878 18.1313C4.1436 17.8768 3.44415 17.4991 2.97254 17.0275C2.50092 16.5558 2.12323 15.8564 1.86873 14.7112C1.61044 13.549 1.5 12.0245 1.5 10Z"
                    className="fill-fg_brand"
                  />
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M1.5 10C1.5 7.97549 1.61044 6.45099 1.86873 5.28878C2.12323 4.1436 2.50092 3.44415 2.97254 2.97254C3.44415 2.50092 4.1436 2.12323 5.28878 1.86873C6.45099 1.61044 7.97549 1.5 10 1.5C12.0245 1.5 13.549 1.61044 14.7112 1.86873C15.8564 2.12323 16.5558 2.50092 17.0275 2.97254C17.4991 3.44415 17.8768 4.1436 18.1313 5.28878C18.3896 6.45099 18.5 7.97549 18.5 10C18.5 12.0245 18.3896 13.549 18.1313 14.7112C17.8768 15.8564 17.4991 16.5558 17.0275 17.0275C16.5558 17.4991 15.8564 17.8768 14.7112 18.1313C13.549 18.3896 12.0245 18.5 10 18.5C7.97549 18.5 6.45099 18.3896 5.28878 18.1313C4.1436 17.8768 3.44415 17.4991 2.97254 17.0275C2.50092 16.5558 2.12323 15.8564 1.86873 14.7112C1.61044 13.549 1.5 12.0245 1.5 10ZM5 10C5 9.58579 5.33579 9.25 5.75 9.25H13.75C14.1642 9.25 14.5 9.58579 14.5 10C14.5 10.4142 14.1642 10.75 13.75 10.75H5.75C5.33579 10.75 5 10.4142 5 10Z"
                    className="fill-fg_brand"
                  />
                </Fragment>
              )}
            </Fragment>
          )}
        </svg>
      </span>
    </label>
  );
});

export default Checkbox;
